import { useLazyQuery } from '@apollo/client';
import { useCallback } from 'react';
import isVerifier from './isVerifier';
import useAppSelector from '../app/redux/useAppSelector';
import { gql } from '../graphql/generated';
import useNavigate from '../utils/router/useNavigate';
import postPath from '../utils/url/postPath';

// eslint-disable-next-line import/no-unused-modules -- exported for testing
export const USE_POST_VERIFY_NAVIGATION_QUERY = gql(/** @complexity 8 */ `
    query usePostVerifyNavigationQuery($postId: ID!) {
      post(id: $postId) {
        id
        content(isLatest: true) {
          id
          version
        }
        verificationEntries {
          id
          version
        }
        ...postPath_post
        ...isVerifier_post
      }
    }
  `);

const usePostVerifyNavigation = (postId: string) => {
  const [loadPostData, { loading }] = useLazyQuery(
    USE_POST_VERIFY_NAVIGATION_QUERY,
    {
      fetchPolicy: 'network-only',
      variables: {
        postId,
      },
    },
  );
  const currentUser = useAppSelector((state) => state.session.user);
  const navigate = useNavigate();

  const postVerifyNavigate = useCallback(
    async ({ replace }: { replace?: boolean } = {}) => {
      const { data } = await loadPostData();
      const post = data?.post;
      if (post == null) return;

      // verificationEntires are in reverse chronological order
      const lastVerificationEntry = post.verificationEntries.at(0);

      if (lastVerificationEntry && isVerifier(post, currentUser)) {
        // We get some false positives like this, i.e. if the version changed due to a format change
        // The upside is that we also catch cases where  a comment was added or resolved.
        // We handle the false positives directly in PostVerifyView, where we load the changes anyways.
        if (post.content.version > lastVerificationEntry.version) {
          navigate(
            `${postPath(post)}/verify?histories=${lastVerificationEntry.version}-${post.content.version}`,
            {
              replace,
            },
          );
          return;
        }
      }

      navigate(postPath(post), {
        state: { verification: true },
        replace,
      });
    },
    [loadPostData, navigate, currentUser],
  );

  return { postVerifyNavigate, loading };
};

export default usePostVerifyNavigation;
